"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const differenceInDays_1 = __importDefault(require("date-fns/differenceInDays"));
const format_1 = __importDefault(require("date-fns/format"));
const isEqual_1 = __importDefault(require("date-fns/isEqual"));
const filter_1 = __importDefault(require("lodash/filter"));
const get_1 = __importDefault(require("lodash/get"));
const groupBy_1 = __importDefault(require("lodash/groupBy"));
const isEmpty_1 = __importDefault(require("lodash/isEmpty"));
const keyBy_1 = __importDefault(require("lodash/keyBy"));
const size_1 = __importDefault(require("lodash/size"));
const mobx_1 = require("mobx");
const getDropoffCharges_1 = __importDefault(require("../clients/branch/getDropoffCharges"));
const API_STATE_1 = __importDefault(require("../constants/app/API_STATE"));
const TAB_INDEX_1 = __importDefault(require("../constants/app/TAB_INDEX"));
const DEFAULT_BRANCH_DATES_1 = __importDefault(require("../constants/date/DEFAULT_BRANCH_DATES"));
const DEFAULT_CURRENCY_1 = __importDefault(require("../constants/date/DEFAULT_CURRENCY"));
const DEFAULT_PICKUP_TIME_1 = __importDefault(require("../constants/date/DEFAULT_PICKUP_TIME"));
const getKSADate_1 = __importDefault(require("../utils/store/getKSADate"));
const getTimeStampForDateTime_1 = __importDefault(require("../utils/store/getTimeStampForDateTime"));
const getUrlFromQuery_1 = __importDefault(require("../utils/store/getUrlFromQuery"));
const mapInitialStateToStoreState_1 = __importDefault(require("../utils/store/mapInitialStateToStoreState"));
const branch_store_1 = __importDefault(require("./branch.store"));
const city_store_1 = __importDefault(require("./city.store"));
const location_store_1 = __importDefault(require("./location.store"));
/**
 * Search Page Store
 * Consumes City and Branch store instances
 * Makes them available via API
 * Maintains Search Page related data for city and branch info
 */
class SearchPageStore {
    constructor(initialState = {}, $env) {
        // ui state
        this.activeTabIndex = 0;
        this.pickupBranch = {};
        this.dropoffBranch = {};
        this.backupDropoffBranch = {};
        this.isPickUpDialogOpen = false;
        this.isDropOffDialogOpen = false;
        this.isSwitchOn = false;
        this.pickupDate = DEFAULT_BRANCH_DATES_1.default.PICKUP;
        this.pickupTime = DEFAULT_PICKUP_TIME_1.default; // 12 hours format
        this.dropoffDate = DEFAULT_BRANCH_DATES_1.default.DROPOFF;
        this.dropoffTime = DEFAULT_PICKUP_TIME_1.default; // 12 hours format
        this.searchDataUpdated = false;
        this.openPickupTimer = false;
        this.openDropoffTimer = false;
        this.minDaysOfDeliveryDropOff = 30;
        this.activeListBy = '';
        this.dropChargesData = {};
        this.apiState = API_STATE_1.default.INITIAL;
        this.errorData = {};
        /**
         * Action to set setActiveTabIndex
         * @param {number} tabIndex
         */
        this.setActiveListBy = (listName) => {
            this.activeListBy = listName;
        };
        /**
         * Returns city list by active tab
         * @param {Array} cityList
         * @returns {Array}
         */
        this.getCityListByActiveTab = () => {
            const cityIdHashmap = (0, keyBy_1.default)(this.activeBranchList, 'cityId');
            return (0, filter_1.default)(this.city.cityList, (city) => cityIdHashmap[city.id]);
        };
        /**
         * Returns branches for a city ID
         * @returns {Array}
         */
        this.getBranchesByCityId = (cityId) => {
            const cityIdHashMap = (0, groupBy_1.default)(this.activeBranchList, 'cityId');
            return cityIdHashMap[cityId];
        };
        /**
         * Returns branch for a branch ID
         * @returns {Object}
         */
        // comment for publish package.
        this.getBranchByBranchId = (branchId) => {
            return this.activeBranchList.find((item) => item.id === branchId);
        };
        /**
         * Makes dropoff charges api call
         * @param {DropoffChargesParamType} reqParam
         * @param {Function} successHandler
         * @param {Function} errorHandler
         */
        this.fetchDropoffCharges = (0, mobx_1.flow)(function* (reqParam, successHandler, errorHandler) {
            try {
                this.apiState = API_STATE_1.default.LOADING;
                this.dropChargesData = {};
                this.errorData = {};
                const response = yield (0, getDropoffCharges_1.default)(reqParam);
                // 200 http codes
                this.onSuccess(response, successHandler);
            }
            catch (err) {
                // all other http codes
                this.onError(err, errorHandler);
            }
        });
        /**
         * on success callback
         * @param data
         */
        this.onSuccess = (data, successHandler) => {
            this.dropChargesData = data;
            this.apiState = API_STATE_1.default.SUCCESS;
            if (successHandler) {
                successHandler(data); // passes the response object back
            }
        };
        /**
         * on error callback
         * @param err
         * @param errorHandler
         */
        this.onError = (err, errorHandler) => {
            this.apiState = API_STATE_1.default.FAILED;
            // when a request is cancelled, Axios promise is rejected
            if (err.response) {
                const errResponse = err.response;
                const { data, status } = errResponse;
                // stores axios error info in observable
                this.errorData = {
                    data,
                    status,
                };
                if (errorHandler) {
                    errorHandler(errResponse);
                }
            }
        };
        /**
         * Action to set setActiveTabIndex
         * @param {number} tabIndex
         */
        this.setActiveTabIndex = (tabIndex) => {
            this.activeTabIndex = tabIndex;
        };
        /**
         * Action to set minDaysOfDeliveryDropOff
         * @param {number} days
         */
        this.setMinDeliveryDays = (days) => {
            this.minDaysOfDeliveryDropOff = days;
        };
        /**
         * Action to set pick up branch
         * @param pickupBranch
         */
        this.setPickUpBranch = (pickupBranch) => {
            this.pickupBranch = Object.assign({}, pickupBranch);
            // if toggle switch is off then
            // pick and drop are same
            if (!this.isSwitchOn) {
                this.dropoffBranch = Object.assign({}, pickupBranch);
            }
        };
        /**
         * Action to set drop off branch
         * @param dropoffBranch
         */
        this.setDropOffBranch = (dropoffBranch) => {
            this.dropoffBranch = Object.assign({}, dropoffBranch);
        };
        /**
         * Action to set when pick up dialog is open
         * @param {boolean} isOpen
         */
        this.setPickUpDialogOpen = (isOpen) => {
            this.isPickUpDialogOpen = isOpen;
        };
        /**
         * Action to set when drop off dialog is open
         * @param {boolean} isOpen
         */
        this.setDropOffDialogOpen = (isOpen) => {
            this.isDropOffDialogOpen = isOpen;
        };
        /**
         * Action to set pick up date
         * Stores date in KSA time zone
         * @param {Date} date
         */
        this.setPickUpDate = (date) => {
            this.pickupDate = (0, getKSADate_1.default)(date);
        };
        /**
         * Action to set drop off date
         * Stores date in KSA time zone
         * @param {Date} date
         */
        this.setDropOffDate = (date) => {
            this.dropoffDate = (0, getKSADate_1.default)(date);
        };
        /**
         * Action to set pick up time
         * @param {string} time
         */
        this.setPickUpTime = (time) => {
            this.pickupTime = time;
        };
        /**
         * Action to set drop off time
         * @param {string} time
         */
        this.setDropOffTime = (time) => {
            this.dropoffTime = time;
        };
        /**
         * Action to set the toggle switch state
         * @param {boolean} newState
         */
        this.setSwitchState = (newState, updateOnlySwitchState = false) => {
            this.isSwitchOn = newState;
            if (!updateOnlySwitchState) {
                // if switch is off & pick up is selected
                if (!newState && this.isPickUpBranchSelected) {
                    // drop off and pick up are same
                    this.backupDropoffBranch = Object.assign({}, this.dropoffBranch);
                    this.dropoffBranch = Object.assign({}, this.pickupBranch);
                }
                // if switch is on and drop off is selected
                if (newState && this.isDropOffBranchSelected) {
                    this.dropoffBranch = this.backupDropoffBranch;
                }
            }
        };
        this.setSearchDataUpdated = (status) => {
            this.searchDataUpdated = status;
        };
        /**
         * Action to set openPickupTimer
         * @param {boolean} status
         */
        this.setOpenPickupTimer = (status) => {
            this.openPickupTimer = status;
        };
        /**
         * Action to set openDropoffTimer
         * @param {boolean} status
         */
        this.setOpenDropoffTimer = (status) => {
            this.openDropoffTimer = status;
        };
        /**
         * Map initial state to store state
         * @param initialState
         */
        this.updateInitialState = (initialState) => {
            (0, mapInitialStateToStoreState_1.default)(this, initialState);
        };
        this.$env = $env;
        this.updateInitialState(initialState);
        // initialize domain stores
        this.city = new city_store_1.default(initialState.city, $env);
        this.branch = new branch_store_1.default(initialState.branch, $env);
        this.pickupBranchStore = new branch_store_1.default(initialState.pickupBranchStore, $env);
        this.dropoffBranchStore = new branch_store_1.default(initialState.dropoffBranchStore, $env);
        this.pickupLocationStore = new location_store_1.default(initialState.pickupLocationStore);
        this.dropoffLocationStore = new location_store_1.default(initialState.dropoffLocationStore);
        // re-initialize the dates since serializing converts the date
        this.pickupDate = DEFAULT_BRANCH_DATES_1.default.PICKUP;
        this.dropoffDate = DEFAULT_BRANCH_DATES_1.default.DROPOFF;
        this.pickupTime = DEFAULT_PICKUP_TIME_1.default;
        this.dropoffTime = DEFAULT_PICKUP_TIME_1.default;
    }
    /**
     * Returns the loading status
     * @returns {Boolean}
     */
    get isLoading() {
        return this.apiState === API_STATE_1.default.LOADING;
    }
    /**
     * Returns if API fetch was success
     * @returns {Boolean}
     */
    get isSuccess() {
        return this.apiState === API_STATE_1.default.SUCCESS;
    }
    /**
     * Returns if API fetch has failed
     * @returns {Boolean}
     */
    get hasFailed() {
        return this.apiState === API_STATE_1.default.FAILED;
    }
    /**
     * Returns dropoffCharge
     * @returns {String}
     */
    get dropoffCharge() {
        const dropOffCharge = (0, get_1.default)(this.dropChargesData, 'dropOff.charge', 0);
        const currency = (0, get_1.default)(this.dropChargesData, 'dropOff.currency', DEFAULT_CURRENCY_1.default);
        return `${dropOffCharge} ${currency}`;
    }
    /**
     * Returns dropoff category type
     * @returns {DropoffCategoryType}
     */
    get dropoffCategoryType() {
        return (0, get_1.default)(this.dropChargesData, 'dropOff.category');
    }
    /**
     * Returns true if pick up branch is selected
     * @returns {boolean}
     */
    get isPickUpBranchSelected() {
        return !(0, isEmpty_1.default)(this.pickupBranch);
    }
    /**
     * Returns true if drop off branch is selected
     * @returns {boolean}
     */
    get isDropOffBranchSelected() {
        return !(0, isEmpty_1.default)(this.dropoffBranch);
    }
    /**
     * Returns the pick up branch id
     */
    get pickupBranchId() {
        return (0, get_1.default)(this.pickupBranch, 'id', -1);
    }
    /**
     * Returns the drop off branch id
     */
    get dropoffBranchId() {
        return (0, get_1.default)(this.dropoffBranch, 'id', -1);
    }
    /**
     * Returns true if pick & drop branch are same
     * @returns {boolean}
     */
    get isDropOffBranchSame() {
        const pickupBranchId = (0, get_1.default)(this.pickupBranch, 'id', -1);
        const dropoffBranchId = (0, get_1.default)(this.dropoffBranch, 'id', -1);
        if (!this.isPickUpBranchSelected) {
            return false;
        }
        if (this.isPickUpBranchSelected && !this.isDropOffBranchSelected) {
            return true;
        }
        return pickupBranchId === dropoffBranchId;
    }
    /**
     * Returns true if pickup and dropoff branch
     * city id's are different
     */
    get isDifferentCity() {
        const pickupCityId = (0, get_1.default)(this.pickupBranch, 'cityId');
        const dropoffCityId = (0, get_1.default)(this.dropoffBranch, 'cityId');
        if (!this.isPickUpBranchSelected) {
            return false;
        }
        if (this.isPickUpBranchSelected && !this.isDropOffBranchSelected) {
            return false;
        }
        return pickupCityId !== dropoffCityId;
    }
    /**
     * Returns the selected date range
     * @returns {Object}
     */
    get selectedDateRange() {
        return {
            from: this.pickupDate,
            to: this.dropoffDate,
        };
    }
    /**
     * Returns the diff in days between pickup and drop dates
     */
    get diffInDays() {
        return (0, differenceInDays_1.default)(this.dropoffDate, this.pickupDate);
    }
    /**
     * Returns true if pick and drop off are same dates
     * I am picking up only the date values since UTC times
     * may be diff for pickup and drop dates
     */
    get isSameDay() {
        const formattedPickupDate = new Date((0, format_1.default)(this.pickupDate, 'MMMM dd, YYY'));
        const formattedDropoffDate = new Date((0, format_1.default)(this.dropoffDate, 'MMMM dd, YYY'));
        return (0, isEqual_1.default)(formattedPickupDate, formattedDropoffDate);
    }
    /**
     * Returns the search query needed to make a fleet search
     * generates all data from store observables/computed props
     * Spaces between branch names replaced with underscore. Fix for
     * LWEB-203
     */
    get searchQueryObject() {
        return {
            pickupBranchId: this.pickupBranchId,
            dropOffBranchId: this.dropoffBranchId,
            pickupBranchName: (0, get_1.default)(this.pickupBranch, 'name', '').replace(/\s+/g, '_'),
            dropOffBranchName: (0, get_1.default)(this.dropoffBranch, 'name', '').replace(/\s+/g, '_'),
            dropOffDate: (0, getTimeStampForDateTime_1.default)(this.dropoffDate, this.dropoffTime),
            pickupDate: (0, getTimeStampForDateTime_1.default)(this.pickupDate, this.pickupTime),
        };
    }
    /**
     * Returns the search query string
     * @returns {string}
     */
    get searchQueryString() {
        return (0, getUrlFromQuery_1.default)(this.searchQueryObject);
    }
    /**
     * Returns active branch list
     * @returns {Array<BranchFactoryType>}
     */
    get branchListByTab() {
        return this.activeTabIndex === 0
            ? this.branch.pickUpBranchList
            : this.branch.carDeliveryBranchList;
    }
    /**
     * Returns active branch list
     * @returns {Array<BranchFactoryType>}
     */
    get activeBranchList() {
        return this.activeListBy === 'listByTab'
            ? this.branchListByTab
            : this.branch.branchList;
    }
    /**
     * Returns true if pick-up tab is active
     */
    get isPickUpTabActive() {
        return this.activeTabIndex === TAB_INDEX_1.default.FIRST;
    }
    /**
     * Returns true if delivery tab is active
     */
    get isDeliveryTabActive() {
        return this.activeTabIndex === TAB_INDEX_1.default.SECOND;
    }
    /**
     * Returns car delivery branch count
     * @returns {Number}
     */
    get carDeliveryCount() {
        return (0, size_1.default)(this.branch.carDeliveryBranchList);
    }
    /**
     * Returns pick-up branch count
     * @returns {Number}
     */
    get pickUpCount() {
        return (0, size_1.default)(this.branch.pickUpBranchList);
    }
}
__decorate([
    mobx_1.observable
], SearchPageStore.prototype, "activeTabIndex", void 0);
__decorate([
    mobx_1.observable
], SearchPageStore.prototype, "pickupBranch", void 0);
__decorate([
    mobx_1.observable
], SearchPageStore.prototype, "dropoffBranch", void 0);
__decorate([
    mobx_1.observable
], SearchPageStore.prototype, "backupDropoffBranch", void 0);
__decorate([
    mobx_1.observable
], SearchPageStore.prototype, "isPickUpDialogOpen", void 0);
__decorate([
    mobx_1.observable
], SearchPageStore.prototype, "isDropOffDialogOpen", void 0);
__decorate([
    mobx_1.observable
], SearchPageStore.prototype, "isSwitchOn", void 0);
__decorate([
    mobx_1.observable
], SearchPageStore.prototype, "pickupDate", void 0);
__decorate([
    mobx_1.observable
], SearchPageStore.prototype, "pickupTime", void 0);
__decorate([
    mobx_1.observable
], SearchPageStore.prototype, "dropoffDate", void 0);
__decorate([
    mobx_1.observable
], SearchPageStore.prototype, "dropoffTime", void 0);
__decorate([
    mobx_1.observable
], SearchPageStore.prototype, "searchDataUpdated", void 0);
__decorate([
    mobx_1.observable
], SearchPageStore.prototype, "openPickupTimer", void 0);
__decorate([
    mobx_1.observable
], SearchPageStore.prototype, "openDropoffTimer", void 0);
__decorate([
    mobx_1.observable
], SearchPageStore.prototype, "minDaysOfDeliveryDropOff", void 0);
__decorate([
    mobx_1.observable
], SearchPageStore.prototype, "activeListBy", void 0);
__decorate([
    mobx_1.observable
    // eslint-disable-next-line prettier/prettier
], SearchPageStore.prototype, "dropChargesData", void 0);
__decorate([
    mobx_1.observable
], SearchPageStore.prototype, "apiState", void 0);
__decorate([
    mobx_1.observable
], SearchPageStore.prototype, "errorData", void 0);
__decorate([
    mobx_1.computed
], SearchPageStore.prototype, "isLoading", null);
__decorate([
    mobx_1.computed
], SearchPageStore.prototype, "isSuccess", null);
__decorate([
    mobx_1.computed
], SearchPageStore.prototype, "hasFailed", null);
__decorate([
    mobx_1.computed
], SearchPageStore.prototype, "dropoffCharge", null);
__decorate([
    mobx_1.computed
], SearchPageStore.prototype, "dropoffCategoryType", null);
__decorate([
    mobx_1.computed
], SearchPageStore.prototype, "isPickUpBranchSelected", null);
__decorate([
    mobx_1.computed
], SearchPageStore.prototype, "isDropOffBranchSelected", null);
__decorate([
    mobx_1.computed
], SearchPageStore.prototype, "pickupBranchId", null);
__decorate([
    mobx_1.computed
], SearchPageStore.prototype, "dropoffBranchId", null);
__decorate([
    mobx_1.computed
], SearchPageStore.prototype, "isDropOffBranchSame", null);
__decorate([
    mobx_1.computed
], SearchPageStore.prototype, "isDifferentCity", null);
__decorate([
    mobx_1.computed
], SearchPageStore.prototype, "selectedDateRange", null);
__decorate([
    mobx_1.computed
], SearchPageStore.prototype, "diffInDays", null);
__decorate([
    mobx_1.computed
], SearchPageStore.prototype, "isSameDay", null);
__decorate([
    mobx_1.computed
], SearchPageStore.prototype, "searchQueryObject", null);
__decorate([
    mobx_1.computed
], SearchPageStore.prototype, "searchQueryString", null);
__decorate([
    mobx_1.computed
], SearchPageStore.prototype, "branchListByTab", null);
__decorate([
    mobx_1.computed
], SearchPageStore.prototype, "activeBranchList", null);
__decorate([
    mobx_1.action.bound
], SearchPageStore.prototype, "setActiveListBy", void 0);
__decorate([
    mobx_1.computed
], SearchPageStore.prototype, "isPickUpTabActive", null);
__decorate([
    mobx_1.computed
], SearchPageStore.prototype, "isDeliveryTabActive", null);
__decorate([
    mobx_1.computed
], SearchPageStore.prototype, "carDeliveryCount", null);
__decorate([
    mobx_1.computed
], SearchPageStore.prototype, "pickUpCount", null);
__decorate([
    mobx_1.action.bound
], SearchPageStore.prototype, "onSuccess", void 0);
__decorate([
    mobx_1.action.bound
], SearchPageStore.prototype, "onError", void 0);
__decorate([
    mobx_1.action.bound
], SearchPageStore.prototype, "setActiveTabIndex", void 0);
__decorate([
    mobx_1.action.bound
], SearchPageStore.prototype, "setMinDeliveryDays", void 0);
__decorate([
    mobx_1.action.bound
], SearchPageStore.prototype, "setPickUpBranch", void 0);
__decorate([
    mobx_1.action.bound
], SearchPageStore.prototype, "setDropOffBranch", void 0);
__decorate([
    mobx_1.action.bound
], SearchPageStore.prototype, "setPickUpDialogOpen", void 0);
__decorate([
    mobx_1.action.bound
], SearchPageStore.prototype, "setDropOffDialogOpen", void 0);
__decorate([
    mobx_1.action.bound
], SearchPageStore.prototype, "setPickUpDate", void 0);
__decorate([
    mobx_1.action.bound
], SearchPageStore.prototype, "setDropOffDate", void 0);
__decorate([
    mobx_1.action.bound
], SearchPageStore.prototype, "setPickUpTime", void 0);
__decorate([
    mobx_1.action.bound
], SearchPageStore.prototype, "setDropOffTime", void 0);
__decorate([
    mobx_1.action.bound
], SearchPageStore.prototype, "setSwitchState", void 0);
__decorate([
    mobx_1.action.bound
], SearchPageStore.prototype, "setSearchDataUpdated", void 0);
__decorate([
    mobx_1.action.bound
], SearchPageStore.prototype, "setOpenPickupTimer", void 0);
__decorate([
    mobx_1.action.bound
], SearchPageStore.prototype, "setOpenDropoffTimer", void 0);
__decorate([
    mobx_1.action.bound
], SearchPageStore.prototype, "updateInitialState", void 0);
exports.default = SearchPageStore;
